import { useEffect, useRef } from "react";

import ProcessEnvironment from "@/constants/ProcessEnvironment";
import { IntercomWidget } from "@/constants/UI";

/**
 * Hook to initialize Intercom on web by loading the JavaScript snippet.
 *
 * This hook should be called early in the app lifecycle (e.g., in RootLayout or a top-level provider)
 * to ensure Intercom is available before any components try to use it.
 *
 * The Intercom snippet is loaded once and cached in a ref to prevent duplicate script tags.
 *
 * See: https://developers.intercom.com/installing-intercom/web/installation/
 */
const useIntercomInitializer = (): void => {
  const isInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    // Only initialize once
    if (isInitializedRef.current) return;

    const appId = ProcessEnvironment.IntercomAppId;

    // Skip if no app ID is configured
    if (!appId) {
      return;
    }

    // Set up Intercom settings
    globalThis.window.intercomSettings = {
      alignment: "left",
      api_base: "https://api-iam.intercom.io",
      app_id: appId,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      hide_default_launcher: true,
      horizontal_padding: IntercomWidget.HorizontalPadding,
      vertical_padding: 20,
    };

    // Load the Intercom script snippet
    // This is the standard Intercom installation code for single-page apps
    // See: https://developers.intercom.com/installing-intercom/web/installation#single-page-app
    (() => {
      const w = globalThis.window;
      const ic = w.Intercom;
      if (typeof ic === "function") {
        ic("reattach_activator");
        ic("update", w.intercomSettings);
      } else {
        const d = document;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const i: any = (...args: unknown[]) => {
          i.c(args);
        };
        i.q = [];
        i.c = (args: unknown[]) => {
          i.q.push(args);
        };
        w.Intercom = i;
        const l = () => {
          const s = d.createElement("script");
          s.type = "text/javascript";
          s.async = true;
          s.src = `https://widget.intercom.io/widget/${appId}`;
          const x = d.getElementsByTagName("script")[0];
          x.parentNode?.insertBefore(s, x);
        };
        if (document.readyState === "complete") {
          l();
        } else {
          w.addEventListener("load", l, false);
        }
      }
    })();

    isInitializedRef.current = true;
  }, []);
};

export default useIntercomInitializer;
